/**
 * useEmbarqueLockStatus — Single source of truth for the post-liquidation lock.
 * ─────────────────────────────────────────────────────────────────────────────
 * Returns whether a given embarque (resolved by id OR by document_id) is liquidated
 * and therefore should render its UI in read-only mode.
 *
 * Frontend uses this to gate inputs, buttons and destructive actions.
 * Backend (triggers + RPC guards) is the ultimate enforcer — this hook
 * is a UX layer to avoid sending requests that will be rejected.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Args {
  /** Embarque id directly. Takes precedence over documentId. */
  embarqueId?: string | null;
  /** Document id; we resolve its embarque_id and then its liquidado flag. */
  documentId?: string | null;
}

interface Result {
  /** True only when the resolved embarque has liquidado = true. */
  isLocked: boolean;
  /** Resolved embarque_id (useful for downstream queries). */
  embarqueId: string | null;
  /** Loading state — UI should treat as "unknown, allow but be cautious". */
  isLoading: boolean;
}

export function useEmbarqueLockStatus({ embarqueId, documentId }: Args): Result {
  const directKey = embarqueId ?? null;
  const docKey = documentId ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ["embarque-lock-status", directKey, docKey],
    enabled: Boolean(directKey || docKey),
    staleTime: 15_000,
    queryFn: async () => {
      let resolvedEmbarqueId = directKey;

      if (!resolvedEmbarqueId && docKey) {
        const { data: doc } = await supabase
          .from("registro_documentos")
          .select("embarque_id")
          .eq("id", docKey)
          .maybeSingle();
        resolvedEmbarqueId = doc?.embarque_id ?? null;
      }

      if (!resolvedEmbarqueId) {
        return { isLocked: false, embarqueId: null };
      }

      const { data: emb } = await supabase
        .from("embarques")
        .select("liquidado")
        .eq("id", resolvedEmbarqueId)
        .maybeSingle();

      return { isLocked: emb?.liquidado === true, embarqueId: resolvedEmbarqueId };
    },
  });

  return {
    isLocked: data?.isLocked ?? false,
    embarqueId: data?.embarqueId ?? null,
    isLoading,
  };
}
